/**
 * Top-N / Bottom-N visibility presets for widget datapoints.
 *
 * These return the HIDDEN list (inverted storage pattern) — i.e., items
 * that should NOT be shown — matching how hiddenDimNDatapoints work.
 *
 * Hidden items are stored as {display, value} objects so that:
 * - `value` is used for unique identification (avoids duplicate display name issues)
 * - `display` is used for human-readable rendering (e.g. tooltips)
 */

import { normalizeDisplayValue, normalizeValue } from "./hiddenDatapoints";

const DEFAULT_N = 3;

/**
 * From a list of {display, value, count} items, return the HIDDEN subset
 * (everything outside the top/bottom N by count).
 */
const pickHiddenByCount = (items, preset, n) => {
  const N = n || DEFAULT_N;
  const isTop = preset === "topN" || preset === "top3";
  const sorted = [...items].sort((a, b) =>
    isTop ? b.count - a.count : a.count - b.count
  );
  const visibleValues = new Set(sorted.slice(0, N).map((item) => item.value));
  return items
    .filter((item) => !visibleValues.has(item.value))
    .map((item) => ({ display: item.display, value: item.value }));
};

/**
 * Collect { display, value, count } items at a given nesting depth.
 * - depth 1: top-level data items
 * - depth 2: children of top-level items (aggregated by value)
 * - depth N: items at Nth nesting level (aggregated by value)
 *
 * Works for any depth — no upper limit.
 */
const collectItemsAtDepth = (data, depth) => {
  if (!data?.length) return [];

  if (depth === 1) {
    return data.map((d) => ({
      display: normalizeDisplayValue(d.display),
      value: normalizeValue(d.value),
      count: d.count || 0,
    }));
  }

  const segmentMap = {};
  const traverse = (items, currentDepth) => {
    if (!items) return;
    items.forEach((item) => {
      if (currentDepth === depth) {
        const value = normalizeValue(item.value);
        if (!segmentMap[value]) {
          segmentMap[value] = {
            display: normalizeDisplayValue(item.display),
            value,
            count: 0,
          };
        }
        segmentMap[value].count += item.count || 0;
      } else {
        traverse(item.children, currentDepth + 1);
      }
    });
  };
  traverse(data, 1);

  return Object.values(segmentMap);
};

/**
 * Generic: compute hidden datapoints for any dimension depth.
 * Works for depth 1, 2, 3, ... N — no hardcoded limit.
 *
 * @param {Array}  data   - Chart data array (hierarchical)
 * @param {string} preset - "topN", "bottomN", "top3", or "bottom3"
 * @param {number} [n]    - Number of items to show (defaults to 3)
 * @param {number} depth  - Nesting depth (1 = top level, 2 = children, etc.)
 * @returns {Array<{display,value}>} - Items to HIDE
 */
export const getHiddenForPresetAtDepth = (data, preset, n, depth) => {
  if (!data?.length || !preset || preset === "all" || preset === "manual") return [];

  const items = collectItemsAtDepth(data, depth);
  if (items.length === 0) return [];

  return pickHiddenByCount(items, preset, n);
};

// Aliases for backward compatibility (used by bar chart's own config forms)
export const getHiddenDim1ForPreset = (data, preset, n) =>
  getHiddenForPresetAtDepth(data, preset, n, 1);

export const getHiddenDim2ForPreset = (data, preset, n) =>
  getHiddenForPresetAtDepth(data, preset, n, 2);

/**
 * Compute hidden datapoints for time-series data (line chart).
 * Data shape: [{timeInterval, dimensions: [{value, display, count}]}, ...]
 * Aggregates dimension counts across all time points before ranking.
 *
 * @param {Array}  data   - Time-series data array
 * @param {string} preset - "topN", "bottomN", "top3", or "bottom3"
 * @param {number} [n]    - Number of items to show (defaults to 3)
 * @returns {Array<{display,value}>} - Items to HIDE
 */
export const getHiddenForPresetFromTimeSeries = (data, preset, n) => {
  if (!data?.length || !preset || preset === "all" || preset === "manual") return [];

  const segmentMap = {};
  data.forEach((timePoint) => {
    timePoint.dimensions?.forEach((dim) => {
      const value = normalizeValue(dim.value);
      if (!segmentMap[value]) {
        segmentMap[value] = {
          display: normalizeDisplayValue(dim.display),
          value,
          count: 0,
        };
      }
      segmentMap[value].count += dim.count || 0;
    });
  });

  const items = Object.values(segmentMap);
  if (items.length === 0) return [];

  return pickHiddenByCount(items, preset, n);
};

